"use client";
import React, { createContext, useCallback, useContext, useMemo, useRef, useTransition } from "react";
import { useRouter } from "next/navigation";

interface InventoryNavContextValue {
  /** True while a list-changing navigation (filter/search/category) is in flight. */
  isNavigating: boolean;
  /** Navigate to an href as a transition so isNavigating reflects the pending state. */
  navigate: (href: string) => void;
  /** Run an existing navigation (e.g. updateFilter/router.push) inside the transition. */
  runNavigation: (fn: () => void) => void;
}

const InventoryNavContext = createContext<InventoryNavContextValue | null>(null);

export function InventoryNavProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [isNavigating, startTransition] = useTransition();

  // Mirror isNavigating into a ref so the guard reads the live value without
  // forcing navigate/runNavigation to be recreated on every transition tick.
  const isNavigatingRef = useRef(false);
  isNavigatingRef.current = isNavigating;

  const navigate = useCallback(
    (href: string) => {
      if (isNavigatingRef.current) return; // ignore new navigations while one is in flight
      startTransition(() => {
        router.push(href, { scroll: false });
      });
    },
    [router]
  );

  const runNavigation = useCallback((fn: () => void) => {
    if (isNavigatingRef.current) return; // ignore new navigations while one is in flight
    startTransition(fn);
  }, []);

  const value = useMemo(
    () => ({ isNavigating, navigate, runNavigation }),
    [isNavigating, navigate, runNavigation]
  );

  return <InventoryNavContext.Provider value={value}>{children}</InventoryNavContext.Provider>;
}

/**
 * Access the inventory navigation state. Falls back to a plain (non-pending)
 * navigation if used outside the provider, so links never break.
 */
export function useInventoryNav(): InventoryNavContextValue {
  const ctx = useContext(InventoryNavContext);
  const router = useRouter();

  if (ctx) return ctx;

  return {
    isNavigating: false,
    navigate: (href: string) => router.push(href, { scroll: false }),
    runNavigation: (fn: () => void) => fn(),
  };
}
